/**
 * RTM OI search (FlightBlender network flight declarations) — ClickUp 86e1w8ude.
 *
 * Calls the SAME-ORIGIN `/oi-api` proxy (worker.ts / Vite dev), which injects
 * the tenant Bearer token server-side. The client never holds the token.
 *
 * GET /flight_declaration_ops/network_flight_declarations_by_view
 *     ?view=<swLat,swLng,neLat,neLng>   (bottom-left , top-right corners)
 *     &end=<ISO>                        (look-ahead window end)
 */
import { config } from '@/lib/config';
import type { DssOi, GeoJsonPolygon } from './types';

export type OiBbox = { swLat: number; swLng: number; neLat: number; neLng: number };

/** One network flight declaration. The API returns a GeoJSON FeatureCollection;
 *  each item here is a Feature flattened so OI fields (from `properties`) sit at
 *  the top level, with the polygon kept under `geometry`. Shape is defensive —
 *  staging returned 0 features at verify time, so exact property names are
 *  inferred from the FlightBlender model; tighten once a populated response is
 *  seen. */
export type OiDeclaration = {
  id?: string | number;
  // Verified property names from a live network-declarations response.
  operational_intent_reference_id?: string;
  operational_intent_reference_manager?: string; // USS name (e.g. "Airmarket")
  operational_intent_reference_uss_base_url?: string;
  operational_intent_state?: string; // e.g. "Accepted"
  time_start?: string;
  time_end?: string;
  geometry?: unknown;
  [k: string]: unknown;
};

export type OiSearchResult = { items: OiDeclaration[]; raw: unknown };

type GeoJsonFeature = {
  id?: string | number;
  properties?: Record<string, unknown>;
  geometry?: unknown;
};

/** Pull declarations out of the API envelope — confirmed GeoJSON
 *  `FeatureCollection`, with fallbacks for other shapes. */
function extractItems(data: unknown): OiDeclaration[] {
  if (Array.isArray(data)) return data as OiDeclaration[];
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    // Verified shape: { type: 'FeatureCollection', features: [...] }
    if (o.type === 'FeatureCollection' && Array.isArray(o.features)) {
      return (o.features as GeoJsonFeature[]).map((f) => ({
        ...(f.properties ?? {}),
        id:
          f.id ??
          (f.properties?.operational_intent_reference_id as string | undefined) ??
          (f.properties?.id as string | number | undefined),
        geometry: f.geometry,
      }));
    }
    for (const k of ['flight_declarations', 'operational_intents', 'results', 'items', 'data']) {
      if (Array.isArray(o[k])) return o[k] as OiDeclaration[];
    }
  }
  return [];
}

/** ISO with seconds precision (the API example has no milliseconds). */
function toApiIso(ms: number): string {
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export async function fetchOiSearch(bbox: OiBbox, endMs: number): Promise<OiSearchResult> {
  const view = `${bbox.swLat},${bbox.swLng},${bbox.neLat},${bbox.neLng}`;
  const params = new URLSearchParams({ view, end: toApiIso(endMs) });
  const url = `${config.oiApi.base}/flight_declaration_ops/network_flight_declarations_by_view?${params.toString()}`;

  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(20_000),
  });
  if (!res.ok) {
    throw new Error(await oiErrorMessage(res));
  }
  const raw = await res.json();
  return { items: extractItems(raw), raw };
}

/** Turn an upstream error response into a concise, user-readable message.
 *  The RTM backend returns a full Django HTML debug page (not JSON) on 5xx —
 *  e.g. a `KeyError 'access_token'` when its own DSS token exchange fails. Show
 *  a clean sentence instead of dumping raw HTML into the panel. */
async function oiErrorMessage(res: Response): Promise<string> {
  const body = await res.text().catch(() => '');
  const isHtml = /^\s*<(?:!doctype|html)/i.test(body);
  // Django puts the exception class in the <title> (e.g. "KeyError at /…").
  const title = isHtml ? (body.match(/<title>([\s\S]*?)<\/title>/i)?.[1] ?? '').trim() : '';
  const detail = (isHtml ? (title.split(/\s+at\s+/i)[0] ?? title) : body)
    .replace(/\s+/g, ' ')
    .trim();

  if (res.status >= 500) {
    return `DSS backend error (${res.status}) — the RTM server couldn't reach the DSS${
      detail ? ` (${detail})` : ''
    }. This is a backend issue; try again shortly.`;
  }
  if (res.status === 401 || res.status === 403) {
    return `OI API not authorised (${res.status}) — the tenant token was rejected.`;
  }
  return `OI API error ${res.status}${detail ? `: ${detail.slice(0, 160)}` : ''}`;
}

const ASTM_TO_DSS_STATUS: Record<string, DssOi['status']> = {
  Accepted: 'planned',
  Activated: 'active',
  Nonconforming: 'active',
  Contingent: 'active',
  Ended: 'completed',
};

export function firstPolygon(geometry: unknown): GeoJsonPolygon | null {
  if (!geometry || typeof geometry !== 'object') return null;
  const g = geometry as { type?: unknown; coordinates?: unknown };
  if (g.type === 'Polygon' && Array.isArray(g.coordinates)) {
    return { type: 'Polygon', coordinates: g.coordinates as GeoJsonPolygon['coordinates'] };
  }
  if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates) && Array.isArray(g.coordinates[0])) {
    return { type: 'Polygon', coordinates: g.coordinates[0] as GeoJsonPolygon['coordinates'] };
  }
  return null;
}

export function validIso(v: unknown): string | null {
  return typeof v === 'string' && Number.isFinite(Date.parse(v)) ? v : null;
}

/** One network declaration → the `DssOi` shape the OI list, detail and map
 *  layer already render. Returns null for anything without a polygon, a
 *  parseable time window or an id — it can't be drawn or placed on the
 *  scrubber. The DSS doesn't publish an operation type. */
export function toDssOi(d: OiDeclaration): DssOi | null {
  const polygon = firstPolygon(d.geometry);
  const start = validIso(d.time_start);
  const end = validIso(d.time_end);
  const id = d.operational_intent_reference_id ?? (d.id != null ? String(d.id) : null);
  if (!polygon || !start || !end || !id) return null;
  const stateRaw = d.operational_intent_state;
  return {
    oi_id: id,
    operator: d.operational_intent_reference_manager || 'Unknown USS',
    op_type: '—',
    status: (stateRaw ? ASTM_TO_DSS_STATUS[stateRaw] : undefined) ?? 'planned',
    start_utc: start,
    end_utc: end,
    polygon,
    ...(d.operational_intent_reference_uss_base_url
      ? { uss_base_url: d.operational_intent_reference_uss_base_url }
      : {}),
    ...(stateRaw ? { state_raw: stateRaw } : {}),
  };
}

export async function searchExternalOis(bbox: OiBbox, endMs: number): Promise<DssOi[]> {
  const { items } = await fetchOiSearch(bbox, endMs);
  return items.map(toDssOi).filter((oi): oi is DssOi => oi !== null);
}
